import { AxiosResponse } from "axios";
import { axiosInstance } from "./axiosInstance";
import {
	ClassroomOptionDTO,
	MentorOptionDTO,
	RecoveryListDTO,
	RecoveryRowDTO,
	RecoveryScheduleBody,
	StudentAttendanceDetailDTO,
	StudentGradesDetailDTO,
	StudentProgressCriteria,
	StudentProgressResponseDTO
} from "../../models/StudentProgressDTO";

const RESOURCE_URL_V2 = "/admin/campus/";

/*
	Same convention as AttendanceService: returns the raw AxiosResponse, the
	controller reads `response.data` ({ rows, summary }).
*/
export class StudentProgressService {

	getStudentProgress = async (campusId: number, criteria: StudentProgressCriteria = {}): Promise<AxiosResponse<StudentProgressResponseDTO>> => {
		const uri = `${RESOURCE_URL_V2}${campusId}/studentProgress`;
		const params: { [key: string]: string | number | boolean } = {};

		if (criteria.courseId != null) params.courseId = criteria.courseId;
		if (criteria.scheduleId != null) params.scheduleId = criteria.scheduleId;
		if (criteria.rotating != null) params.rotating = criteria.rotating ? 1 : 0;
		if (criteria.alert) params.alert = criteria.alert;
		if (criteria.q) params.q = criteria.q;
		if (criteria.includeInactive) params.includeInactive = 1;
		if (criteria.includeInactiveStudents) params.includeInactiveStudents = 1;
		if (criteria.mentorId != null) params.mentorId = criteria.mentorId;

		const response = await axiosInstance.get<StudentProgressResponseDTO>(uri, { params });
		return response;
	}

	getAttendances = async (campusId: number, enrollmentId: number): Promise<AxiosResponse<StudentAttendanceDetailDTO>> => {
		const uri = `${RESOURCE_URL_V2}${campusId}/studentProgress/_attendances`;
		const response = await axiosInstance.get<StudentAttendanceDetailDTO>(uri, { params: { enrollmentId } });
		return response;
	}

	/** Detalle de la nota final (R2): categorías con peso y promedio, y cada evaluación. */
	getGrades = async (campusId: number, enrollmentId: number): Promise<AxiosResponse<StudentGradesDetailDTO>> => {
		const uri = `${RESOURCE_URL_V2}${campusId}/studentProgress/_grades`;
		return axiosInstance.get<StudentGradesDetailDTO>(uri, { params: { enrollmentId } });
	}

	/* Por reponer (FASE 3). status: 0..3 o "open" (Por reponer + Programada). q: busca alumna o curso en el servidor (R2). */
	getRecoveries = async (campusId: number, filters: { status?: string; enrollmentId?: number; scheduleId?: number; courseId?: number; q?: string; page?: number; perPage?: number } = {}): Promise<AxiosResponse<RecoveryListDTO>> => {
		const params: { [key: string]: string | number } = {};
		if (filters.courseId) params.courseId = filters.courseId;
		if (filters.q && filters.q.trim()) params.q = filters.q.trim();
		if (filters.page) params.page = filters.page;
		if (filters.perPage) params.perPage = filters.perPage;
		if (filters.status != null && filters.status !== "") params.status = filters.status;
		if (filters.enrollmentId) params.enrollmentId = filters.enrollmentId;
		if (filters.scheduleId) params.scheduleId = filters.scheduleId;
		return axiosInstance.get<RecoveryListDTO>(`${RESOURCE_URL_V2}${campusId}/studentProgress/_recoveries`, { params });
	}

	scheduleRecovery = async (campusId: number, body: RecoveryScheduleBody): Promise<AxiosResponse<RecoveryRowDTO>> =>
		axiosInstance.put<RecoveryRowDTO>(`${RESOURCE_URL_V2}${campusId}/studentProgress/_recoverySchedule`, body);

	/** 422 si la sesión de reposición aún no ocurre. */
	markRecovered = async (campusId: number, body: RecoveryScheduleBody): Promise<AxiosResponse<RecoveryRowDTO>> =>
		axiosInstance.put<RecoveryRowDTO>(`${RESOURCE_URL_V2}${campusId}/studentProgress/_recovered`, body);

	waiveRecovery = async (campusId: number, id: number, note: string): Promise<AxiosResponse<RecoveryRowDTO>> =>
		axiosInstance.put<RecoveryRowDTO>(`${RESOURCE_URL_V2}${campusId}/studentProgress/_waive`, { id, note });

	/** Todas las aulas del plantel (sección classrooms, FASE 4). */
	getClassrooms = async (campusId: number): Promise<AxiosResponse<ClassroomOptionDTO[]>> =>
		axiosInstance.get<ClassroomOptionDTO[]>(`${RESOURCE_URL_V2}${campusId}/classrooms`, { params: { all: 1 } });

	/** Maestras del plantel (sección schedules). */
	getMentors = async (campusId: number): Promise<AxiosResponse<MentorOptionDTO[]>> =>
		axiosInstance.get<MentorOptionDTO[]>(`${RESOURCE_URL_V2}${campusId}/schedules/_mentors`);
}
